import { AnimatePresence } from "framer-motion";
import React, { useRef, useState } from "react";

import { AnimationStyle, FramerMotionWrapper } from "components/ui";
import {
    DeleteButton,
    ErrorMessage,
    FilesWrapper,
    InputLabel,
    InputLabelIconWrapper,
    InputLabelText,
    LabelsWrapper,
    StyledFileUpload,
    StyledFileUploadLabel,
    StyledFileUploadSubLabel,
    StyledInput,
    StyledUploadButton,
    UploadedErrorMessage,
    UploadedFile,
    UploadedFileName,
    UploadedFileSize
} from "components/shared/fileUpload/FileUpload.styled";
import { FILE, Icon, TRASH } from "components/ui/icon";

import { formatBytes } from "helpers/format/fileSize";
import { useMediaQueryContext } from "helpers/hooks";

export type FileUploadProps = {
    /** WARNING: DO NOT pass className manually - This has to be set in this way to fix a caveat with typescript and styled components. */
    className?: string;
    /** Input disabled or not. */
    disabled?: boolean;
    /** Style input with error styles, without the message */
    hasError?: boolean;
    /** Error message for the field */
    errorMessage?: string;
    /** Label for the input. */
    label?: string;
    /** Sub title under the label */
    subLabel?: string;
    /** name used for tracking the value of the input, used by react-hook-forms. */
    name: string;
    /** text to be displayed on the upload drag/drop field */
    uploadFieldText: string;
    /** text to be displayed on the upload button */
    buttonText: string;
    /** Return function when new files get added / old files removed */
    onChange(newFiles: Array<File>): void;
    /** Current list of files */
    currentFiles?: Array<File>;
    /** Array with allowed file types (example: image/png) */
    allowedFileTypes?: Array<string>;
    /** Max file size, not for single items but for the entire upload. */
    maxFileSize: number;
};

export function FileUpload({
    className,
    disabled = false,
    errorMessage,
    label,
    subLabel,
    uploadFieldText,
    buttonText,
    name,
    onChange,
    currentFiles = [],
    allowedFileTypes = [],
    maxFileSize,
    ...otherProps
}: FileUploadProps): JSX.Element {
    const { isMobileView } = useMediaQueryContext();

    const [fileErrors, setFileErrors] = useState<
        Array<{
            name: string;
            errorMessage?: string;
        }>
    >([]);

    /* 1MB is 1048576 bytes */
    const maxFileSizeInBytes = maxFileSize * 1048576;

    /* Getting the total file size of the files that are already uploaded */
    let currentFilesTotalFilesize = 0;
    currentFiles?.forEach(file => {
        currentFilesTotalFilesize = currentFilesTotalFilesize + file.size;
    });

    /* Switching allowed filetypes from an array to string so we can pass it
     *  to the input component. */
    const allowedFiletypesString = allowedFileTypes?.join(",");

    let allowedFiletypesArray: Array<string> = [];
    allowedFileTypes.forEach(allowedFiletype => {
        if (allowedFiletype.includes(",")) {
            const array = allowedFiletype.split(",");
            allowedFiletypesArray = [...allowedFiletypesArray, ...array];
        } else {
            allowedFiletypesArray = [...allowedFiletypesArray, allowedFiletype];
        }
    });

    const inputElement = useRef(null);

    const onFilesUploaded = (files: Array<File>) => {
        let filesToAdd: Array<File> = [];
        let filesToAddTotalFilesize = 0;
        let filesWithErrors: Array<{ name: string; errorMessage?: string }> =
            [];

        Object.values(files).forEach(file => {
            const checkedFile = fileChecker(file);

            if (checkedFile.hasError) {
                filesWithErrors = [
                    ...filesWithErrors,
                    { errorMessage: checkedFile.errorMessage, name: file.name }
                ];
            } else {
                filesToAdd = [...filesToAdd, file];
                filesToAddTotalFilesize = filesToAddTotalFilesize + file.size;
            }
        });

        /* Checking if the files that are ready to upload and the current files
         * together are under the maxFileSize limit */
        if (
            currentFilesTotalFilesize + filesToAddTotalFilesize >
            maxFileSizeInBytes
        ) {
            setFileErrors([
                ...filesWithErrors,
                {
                    errorMessage: `Totale bestandsgrootte overschreven, totaal: ${maxFileSize}MB`,
                    name: ""
                }
            ]);

            /* Checking if there are more than 2 files uploaded */
        } else if (currentFiles.length + filesToAdd.length > 2) {
            setFileErrors([
                ...fileErrors,
                {
                    errorMessage: "Maximaal 2 bestanden toegestaan",
                    name: ""
                }
            ]);
        } else {
            setFileErrors(filesWithErrors);

            onChange([...currentFiles, ...filesToAdd]);
        }

        /* Clearing the input after files have been uploaded */
        if (inputElement.current) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            inputElement.current.value = "";
        }
    };

    const fileChecker = (newFile: File) => {
        /* Checking if there are fileType restrictions set */
        if (allowedFileTypes?.length > 0) {
            /* Checking if the file has an accepted fileType */
            const fileExtentionFound = allowedFiletypesArray.find(allowedType =>
                newFile.type.startsWith(allowedType.replace("*", ""))
            );

            if (!fileExtentionFound) {
                return {
                    errorMessage: "Bestandstype wordt niet ondersteund",
                    hasError: true
                };
            }
        }

        /* Checking if the newFile is a duplicate */
        const foundDuplicate = currentFiles.find(
            file =>
                file.name === newFile.name &&
                file.lastModified === newFile.lastModified
        );

        if (foundDuplicate) {
            return {
                errorMessage: "Dit bestand staat al in de upload lijst",
                hasError: true
            };
        }

        /* Check the size of a single file */
        if (newFile.size > maxFileSizeInBytes) {
            return {
                errorMessage: `Bestandsgrootte overschreden, maximaal: ${maxFileSize}MB`,
                hasError: true
            };
        }

        return {
            hasError: false
        };
    };

    const removeUploadedFile = (deleteFile: File) => {
        const newCurrentFiles = [...currentFiles];

        const indexToDelete = newCurrentFiles.findIndex(
            file =>
                file.name === deleteFile.name &&
                file.lastModified === deleteFile.lastModified
        );

        newCurrentFiles.splice(indexToDelete, 1);

        onChange(newCurrentFiles);
    };

    return (
        <StyledFileUpload className={className}>
            <LabelsWrapper>
                <div>
                    <StyledFileUploadLabel>{label}</StyledFileUploadLabel>
                    <StyledFileUploadSubLabel>
                        {subLabel}
                    </StyledFileUploadSubLabel>
                </div>
                <StyledUploadButton
                    hasError={!!errorMessage}
                    buttonType="ghost-dark"
                    // This on click is an "ignore" mess, but it does work.
                    // But why not use another <label>? Well, because it's not working with our button component.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    onClick={e => {
                        if (e) {
                            // eslint-disable-next-line
                            e.preventDefault();
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                            inputElement.current?.click();
                        }
                    }}
                >
                    {buttonText}
                </StyledUploadButton>
            </LabelsWrapper>
            {!isMobileView && (
                <InputLabel htmlFor={name} hasError={!!errorMessage}>
                    <InputLabelIconWrapper>
                        <Icon
                            icon={FILE}
                            size={24}
                            color="var(--color-grey-60)"
                        />
                    </InputLabelIconWrapper>
                    <InputLabelText>{uploadFieldText}</InputLabelText>
                </InputLabel>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <StyledInput
                id={name}
                ref={inputElement}
                accept={allowedFiletypesString}
                type="file"
                {...otherProps}
                disabled={disabled}
                name={name}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onChange={e => onFilesUploaded(e.target.files)}
                multiple
            />
            <FilesWrapper>
                {currentFiles.map(uploadedFile => (
                    <AnimatePresence
                        key={
                            uploadedFile.name +
                            uploadedFile.lastModified.toString()
                        }
                    >
                        <FramerMotionWrapper
                            animationType={AnimationStyle.FadeInFromBottom}
                        >
                            <UploadedFile>
                                <UploadedFileName>
                                    {uploadedFile.name}
                                </UploadedFileName>
                                <UploadedFileSize>
                                    {formatBytes(uploadedFile.size, 2)}
                                </UploadedFileSize>
                                <DeleteButton
                                    onClick={() =>
                                        removeUploadedFile(uploadedFile)
                                    }
                                >
                                    <Icon
                                        icon={TRASH}
                                        size={24}
                                        color="var(--color-red-50)"
                                    />
                                </DeleteButton>
                            </UploadedFile>
                        </FramerMotionWrapper>
                    </AnimatePresence>
                ))}
            </FilesWrapper>
            <FilesWrapper>
                {fileErrors.map(errorItem => (
                    <AnimatePresence key={errorItem.name + "-error"}>
                        <FramerMotionWrapper
                            animationType={AnimationStyle.FadeInFromBottom}
                        >
                            <UploadedFile key={errorItem.name}>
                                {errorItem.name && (
                                    <UploadedFileName>
                                        {errorItem.name}
                                    </UploadedFileName>
                                )}
                                <UploadedErrorMessage>
                                    {errorItem.errorMessage}
                                </UploadedErrorMessage>
                            </UploadedFile>
                        </FramerMotionWrapper>
                    </AnimatePresence>
                ))}
            </FilesWrapper>
        </StyledFileUpload>
    );
}
